import {startOfDay, addDays} from 'date-fns';

import {BigCalendarEvent} from '../types/BigCalendarEvent';
import {calculateEventLayout} from './calculateEventLayout';
import {isDateInEventRange} from './isDateInEventRange';
import {isMultiDayEvent} from './isMultiDayEvent';

type GetEventsForDateReturnType = {
  regularEvents: BigCalendarEvent[];
  allDayEvents: (BigCalendarEvent & {allDayPosition: number})[];
};

export function getEventsForDate(
  events: BigCalendarEvent[],
  date: Date
): GetEventsForDateReturnType {
  const processedRegularEvents: BigCalendarEvent[] = [];
  const allDayEvents: (BigCalendarEvent & {allDayPosition: number})[] = [];

  events.forEach((event) => {
    const isEventInRange = isDateInEventRange(event.start, event.end, date);

    if (!isEventInRange) {
      return;
    }

    const isMultiDay = isMultiDayEvent(event.start, event.end);

    // All-day events should only be single-day events
    if (event.isAllDay && !isMultiDay) {
      allDayEvents.push({...event, allDayPosition: allDayEvents.length});
      return;
    }

    // Regular events (including multi-day events) - show on timeline with actual times
    if (!event.isAllDay || isMultiDay) {
      let processedEvent = {...event};

      // For multi-day events, stretch to full day on timeline
      if (isMultiDay) {
        const dayStart = startOfDay(date);
        const dayEnd = startOfDay(addDays(date, 1)); // End of day (24:00 = 0:00 next day)

        // For multi-day events, always stretch to full day on each day they appear
        // This ensures proper visualization across multiple days
        let eventStart = dayStart;
        let eventEnd = dayEnd;
        let isActualEventEnd = false; // Track if this segment shows the real event end

        // If event starts later than dayStart on this day, use event start time
        if (event.start > dayStart && event.start < dayEnd) {
          eventStart = event.start;
        }

        // If event ends earlier than dayEnd on this day, use event end time
        if (event.end > dayStart && event.end < dayEnd) {
          eventEnd = event.end;
          isActualEventEnd = true; // This segment shows the actual event end
        }

        processedEvent = {
          ...event,
          start: eventStart,
          end: eventEnd,
          originalStart: event.originalStart ?? event.start,
          originalEnd: event.originalEnd ?? event.end,
          isAllDay: false, // Multi-day events are never all-day, always show on timeline
          isActualEventEnd, // Add flag to indicate if resize handle should be shown
        };
      } else {
        // Single-day events always show their actual end
        processedEvent = {
          ...event,
          isActualEventEnd: true,
        };
      }

      processedRegularEvents.push(processedEvent);
    }
  });

  return {
    regularEvents: calculateEventLayout(processedRegularEvents),
    allDayEvents,
  };
}
